import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { Router } from '@angular/router';
import { io } from 'socket.io-client';
import { BehaviorSubject, Subject, throwError } from 'rxjs';
import { Location } from '@angular/common';
import { environment } from '../environments/environment';
import { ToastrService } from 'ngx-toastr';
import { MessagingService } from './messaging.service';

@Injectable({
  providedIn: 'root'
})
export class HttpService {

  private _type;
  private windows: any = [];
  private company_uuid = null;
  private permission = null;
  public flattened_permission: any = {};
  public url = null;
  private socket: any = null;
  private socketConnection = new BehaviorSubject<any>(null);
  private newMessageSubject = new Subject<any>();
  private newDepositSubject = new BehaviorSubject<any>(null);
  private newWithdrawSubject = new BehaviorSubject<any>(null);
  private socketReconnected = new BehaviorSubject<boolean>(false);
  private socketStatus = false;
  private recentMessageIds = new Set<string>();
  private recentFinancialNotificationIds = new Set<string>();
  private chatUnreadSince = new Map<string, { since: Date; name: string; room_id: string }>();
  private chatReminderSubject = new Subject<any[]>();
  private chatReminderInterval: any = null;
  private audioCache: Map<string, HTMLAudioElement> = new Map();

  constructor(private http: HttpClient, private cs: CookieService, private router: Router, private location: Location, private toastr: ToastrService, private messagingService: MessagingService) {
    this.company_uuid = new BehaviorSubject<string>('');
    this.permission = new BehaviorSubject<object>(null);
    this.url = environment.url;

    if (environment.url.indexOf(":1113") > -1) {
      this._type = 1;
    } else {
      this._type = 2;
    }

  }

  private _company_uuid = null;
  private company: any = [];
  private companyChanges = new BehaviorSubject<any[]>([]);
  public image = null;

  public get viewAs(): any {
    try { return JSON.parse(sessionStorage.getItem('admin-view-as') || 'null'); }
    catch { return null; }
  }

  public get isViewingUser(): boolean { return !!this.viewAs; }

  public async startViewAs(userUuid: string) {
    const result: any = await this.post('/admin/view-as/start', { edit_user_uuid: userUuid });
    if (!result?.status) { this.notify(result?.message || 'Unable to start user view.'); return; }
    sessionStorage.setItem('admin-view-as', JSON.stringify({ ...result, returnUrl: this.router.url }));
    const grants: any = await this.post('/admin/user/permission/hidden', { company_uuid: result.company_uuid });
    const page = this.viewAsReportPages.find(p => grants?.[p] == 1) || 'unauthorized';
    window.location.assign('/lo/' + result.company_uuid + '/' + page);
  }

  public readonly viewAsReportPages = ['summary-report-v2', 'summary-report', 'daily-sales-report',
    'weekly-account', 'consolidated-summary-report', 'consolidated-daily-sales-report'];

  public async exitViewAs() {
    const view = this.viewAs;
    if (!view) return;
    const actor = this.getActorAccess();
    try {
      await this.http.post(this.url + '/admin/view-as/end', {
        user_uuid: actor.uuid, token: actor.token, view_token: view.view_token
      }).toPromise();
    } finally {
      sessionStorage.removeItem('admin-view-as');
      window.location.assign(typeof view.returnUrl === 'string' && /^\/lo\//.test(view.returnUrl) ? view.returnUrl : '/lo');
    }
  }

  async get(path: string) {
    if (this.isViewingUser) throw new Error('This request is unavailable in read-only user view.');
    return this.http.get(this.url + path).toPromise();
  }

  async post(path: string, data = {}) {

    data = { ...data };
    if (data['consolidated_wallet_context'] === undefined && /^\/lo\/[^/]+\/consolidated-wallet(?:[/?]|$)/.test(this.router.getCurrentNavigation()?.finalUrl?.toString() || this.router.url)) {
      data['consolidated_wallet_context'] = true;
    }

    const walletUrl = this.router.getCurrentNavigation()?.finalUrl?.toString() || this.router.url;
    const walletMatch = /^\/lo\/([^/]+)\/consolidated-wallet(?:[/?]|$)/.exec(walletUrl);
    if (walletMatch && !data['report_company_uuid']) {
      data['report_company_uuid'] = this.router.parseUrl(walletUrl).queryParams['report_company'] || decodeURIComponent(walletMatch[1]);
    }

    const access = this.getAccess();

    if (access['uuid']) {
      data['user_uuid'] = access['uuid'];
      if (data['token'] === undefined && path.startsWith('/admin/')) data['token'] = access['token'];
    }

    if (!data['company_uuid'] && this._company_uuid) {
      data['company_uuid'] = this._company_uuid;
    }

    if (this.isViewingUser) {
      const actor = this.getActorAccess();
      data = { user_uuid: actor.uuid, token: actor.token, view_token: this.viewAs.view_token,
        read_path: path.startsWith('/admin/') ? path.slice(6) : '', payload: data };
      path = '/admin/view-as/read';
    }

    var promise = new Promise((resolve, reject) => {
      const p = this.http.post(this.url + path, data);

      if (!p) {
        resolve(null)
        return;
      }

      const y = p.toPromise();

      y.then((data) => {
        if (this.isViewingUser && data?.['status'] === false && data?.['message']) this.notify(data['message']);
        if (data['status'] && data['message']) {
          this.notify(data['message']);
        }
        resolve(data)
      }).catch((error) => {
        this.handleError(error);
        resolve(null)
      })
    });

    return promise;
  }

  postStream(path: string, data = {}) {
    if (this.isViewingUser) return throwError(() => new Error('Streaming and chat are disabled in read-only user view.'));

    const access = this.getAccess();

    if (access['uuid']) {
      data['user_uuid'] = access['uuid'];
      if (path.startsWith('/admin/chat/')) data['token'] = access['token'];
    }

    if (!data['company_uuid'] && this._company_uuid) {
      data['company_uuid'] = this._company_uuid;
    }

    return this.http.post(this.url + path, data, { responseType: 'text', observe: 'events', reportProgress: true, headers: { 'X-Content-Type-Options': 'nosniff' } });
  }

  private extractErrorMessage(body: any): string {
    if (body == null) return 'Unknown error';
    if (typeof body === 'string') return body;
    if (typeof body === 'object') {
      return body.message || body.error || body.descp || JSON.stringify(body);
    }
    return String(body);
  }

  private handleError(error: HttpErrorResponse) {
    const message = this.extractErrorMessage(error.error);
    if (error.status === 403) {
      alert(message);
      this.clearAccess();
      this.navigate(['lr'])
    } else if (error.status === 0) {
      console.error('An error occurred:', error.error);
    } else {
      alert(message);
      console.error(`Backend returned code ${error.status}, error was:`, error.error);
    }
  }

  public setAccess(uuid, username, name, token, is_admin, is_mario) {
    const now = new Date();
    now.setTime(now.getTime() + (1 * 24 * 3600 * 1000));
    this.cs.set('uuid', uuid, now, '/')
    this.cs.set('username', username, now, '/')
    this.cs.set('name', name, now, '/');
    this.cs.set('token', token, now, '/');
    if (is_admin) {
      this.cs.set('is_admin', is_admin, now, '/');
      this.cs.set('is_mario', is_mario, now, '/');
    }
  }

  public getAccess() {
    const view = this.viewAs;
    if (view) return { uuid: 'view-as-user', username: view.target.username, name: view.target.name,
      token: view.view_token, is_admin: view.target.is_admin, is_mario: '0' };
    return this.getActorAccess();
  }

  private getActorAccess() {
    const uuid = this.cs.get('uuid')
    const username = this.cs.get('username')
    const name = this.cs.get('name');
    const token = this.cs.get('token');
    const is_admin = this.cs.get('is_admin') ? this.cs.get('is_admin') : null;
    const is_mario = this.cs.get('is_mario') ? this.cs.get('is_mario') : null;

    const get = { uuid, username, name, token };

    if (is_admin) {
      get['is_admin'] = is_admin;
      get['is_mario'] = is_mario;
    }

    return get;
  }

  public async getPermission() {
    const rtn: any = await this.post('/admin/user/permission');
    this.flattened_permission = rtn.flattened_permission || {};
    return rtn;
  }

  public async getHiddenPermission() {
    return await this.post('/admin/user/permission/hidden');
  }

  public clearAccess() {
    this.cs.delete('uuid', '/');
    this.cs.delete('username', '/');
    this.cs.delete('name', '/');
    this.cs.delete('token', '/');
    this.cs.delete('is_admin', '/');
    this.cs.delete('is_mario', '/');

    this._company_uuid = null;
    this.company = [];
  }

  public navigate(path, object = {}) {
    this.router.navigate(path, { state: object });
  }

  public naviBack() {
    this.location.back()
  }

  public notify(content: any) {
    this.toastr.info(content);
  }

  public get isUnityShareholder(): boolean {
    return this.company.some(c => c.unity_shareholder === true);
  }

  public getCompany() {
    return this.company;
  }

  public getCompanyChanges() { return this.companyChanges.asObservable(); }

  public async setCompany() {
    const companies = await this.post('/admin/user/company');
    if (Array.isArray(companies)) {
      this.company = companies;
      this.companyChanges.next(companies);
    }
    return this.company;
  }

  public getSelectedCompany() {
    return this.company_uuid;
  }

  public getStaticSelectedCompany() {
    return this._company_uuid;
  }

  public setSelectedCompany(company_uuid = null) {

    if (this._company_uuid && this._company_uuid == company_uuid) {
      return;
    }

    if (company_uuid && this.company.length > 0) {
      const uuid_list = this.company.map((e) => e.uuid);

      if (uuid_list.indexOf(company_uuid) > -1) {
        this._company_uuid = company_uuid;
        this.company_uuid.next(company_uuid);
        //this.navigate(['lo', this.company_uuid, 'dashboard'])
        return;
      }

    }

    if ((!this.company_uuid || this.company_uuid == undefined) && this.company.length > 0) {
      this._company_uuid = this.company[0]['uuid'];
      this.company_uuid.next(this.company[0]['uuid']);
      this.navigate(['lo', this.company_uuid, 'dashboard'])

      return;
    }

    if (company_uuid) {
      this.navigate(['lo', company_uuid, 'unauthorized'])
      this._company_uuid = null;
      this.company_uuid.next('');

    }

  }

  public getSelecetdCompanyDetailListener() {
    return this.permission;
  }

  public getSelecetdCompanyDetail() {
    if (!this._company_uuid) {
      return false;
    }

    const filter = this.company.filter((e) => {
      return e.uuid == this._company_uuid;
    });

    if (!filter || filter.length == 0 || !filter[0]) {
      return false;
    }

    this.permission.next(filter[0]);

    return filter[0];
  }

  public async getGame() {
    return await this.post('/admin/setting/game/get');
  }

  public fileChangeEvent(fileInput: any) {

    return new Promise(function (resolve, reject) {

      if (fileInput.target.files && fileInput.target.files[0]) {
        // Size Filter Bytes
        const max_size = 2097152;
        const allowed_types = ['image/png', 'image/jpeg', 'image/svg+xml'];
        const max_height = 15200;
        const max_width = 25600;

        if (fileInput.target.files[0].size > max_size) {
          return reject('Maximum size allowed is ' + max_size / 1000 + 'Mb');
        }

        if (allowed_types.indexOf(fileInput.target.files[0].type) <= -1) {
          return reject('Only Images are allowed ( JPG | PNG | SVG )');
        }
        const reader = new FileReader();
        reader.onload = (e: any) => {
          const image = new Image();
          image.src = e.target.result;
          image.onload = rs => {
            const img_height = rs.currentTarget['height'];
            const img_width = rs.currentTarget['width'];

            if (img_height > max_height && img_width > max_width) {
              return reject('Maximum dimentions allowed ' +
                max_height +
                '*' +
                max_width +
                'px');
            } else {
              const imgBase64Path = e.target.result;
              return resolve(imgBase64Path);
            }
          };
        };

        reader.readAsDataURL(fileInput.target.files[0]);
      } else {
        return reject('No File Error');
      }

    });

  }

  // public socketEmit(type:string, data:any = {}, company_id) {

  //   if (this.getSelectedCompany()) {
  //     data.type = 'admin';
  //     data.data_type = this._type;
  //     data.company_uuid = company_id;
  //     this.socket.emit(type, data)
  //   }

  // }

  createIdentifier(): string {
    const stringArr = [];
    for (let i = 0; i < 4; i++) {
      const S4 = (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
      stringArr.push(S4);
    }
    const identifier = stringArr.join('-');

    const now = new Date();
    now.setTime(now.getTime() + (365 * 24 * 3600 * 1000));
    this.cs.set('iden', identifier, now, '/');

    return identifier;
  }

  getIdentifier() {
    const iden = this.cs.get('iden');

    var get: any = { status: false };
    if (iden) {
      get = { status: true, identifier: iden };
    }
    return get;
  }

  openWindow(path) {
    let win = window.open(path, "_blank");
    this.windows.push(win)
  }

  closeAllWindow() {
    for (var x = 0; x < this.windows.length; x++) {
      try {
        this.windows[x].close();
      } catch ($e) {

      }
    }
  }

  getSocket() {
    return this.socket;
  }

  getNewMessage() {
    return this.newMessageSubject;
  }

  getNewDeposit() {
    return this.newDepositSubject;
  }

  getNewWithdraw() {
    return this.newWithdrawSubject;
  }

  getSocketObserver() {
    return this.socketConnection;
  }

  getSocketStatus() {
    return this.socketStatus;
  }

  getSocketReconnected() {
    return this.socketReconnected.asObservable();
  }

  private dedupMessage(id: string): boolean {
    if (this.recentMessageIds.has(id)) return true;
    this.recentMessageIds.add(id);
    if (this.recentMessageIds.size > 100) {
      const first = this.recentMessageIds.values().next().value;
      if (first) this.recentMessageIds.delete(first);
    }
    return false;
  }

  markMessageSeen(id: string) {
    this.recentMessageIds.add(id);
    if (this.recentMessageIds.size > 100) {
      const first = this.recentMessageIds.values().next().value;
      if (first) this.recentMessageIds.delete(first);
    }
  }

  initSocket(company_uuid: string) {

    if (!company_uuid) {
      return;
    }

    this.socket?.disconnect();
    this.socket = null;

    if (this.isUnityShareholder || this.isViewingUser) return;

    this.socket = io(environment.socket_url, {
      path: '/live-chat',
      transports: ['websocket', 'polling'],
      upgrade: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30000,
      timeout: 20000,
      query: {
        company_uuid: company_uuid,
        user_uuid: this.getAccess().uuid,
        token: this.getAccess().token
      }
    });

    this.socket.on('connect', () => {
      this.socketStatus = true;
      this.socketConnection.next(this.socketStatus);
    });

    this.socket.on('disconnect', () => {
      this.socketStatus = false;
      this.socketConnection.next(this.socketStatus);
    });

    this.socket.io.on('reconnect', () => {
      this.socketReconnected.next(true);
    });

    this.socket.on('new_chat_message', (message: any) => {
      if (message && !this.dedupMessage(message.uuid || message.id)) {
        this.newMessageSubject.next(message);

        // Play notification sound for incoming user/customer messages
        if (message.user_type === 'user') {
          this.newMessageBeep();
          this.trackUnreadChat(message.room, message.user || message.contact_name || 'Anonymous');
        }
      }
    });

    this.socket.on('new_deposit', (message: any) => {
      this.receiveFinancialNotification('new_deposit', message);
    });

    this.socket.on('new_withdraw', (message: any) => {
      this.receiveFinancialNotification('new_withdraw', message);
    });

    this.socket.on('admin_notification', (message: any) => {
      if (message) {
        this.notifyPersistentError(message.message || message.descp || 'Admin notification', message.title || 'Notification');
        this.newMessageBeep('notification');
      }
    });
  }

  initNotification() {
    this.messagingService.getNotifications().subscribe(notification => {
      if (notification) {
        notification = notification.notification;
        let body = JSON.parse(notification.body);
        switch (notification.title) {
          case 'new_chat_message':
            // Dedup: skip if socket already delivered this message
            if (this.dedupMessage(body.uuid || body.id)) return;
            this.newMessageSubject.next(body);
            if (body.user_type === 'user') {
              this.newMessageBeep();
              this.trackUnreadChat(body.room, body.user || body.contact_name || 'Anonymous');
            }
            return;
          case 'new_deposit':
          case 'new_withdraw':
            this.receiveFinancialNotification(notification.title, body);
            return;
          case 'admin_notification':
            this.notifyPersistentError(body.message || body.descp || 'Admin notification', body.title || 'Notification');
            this.newMessageBeep('notification');
            return;
        }

        this.newMessageBeep();
      }
    });
  }

  private receiveFinancialNotification(event: 'new_deposit' | 'new_withdraw', message: any) {
    if (!message) return;
    const id = message.uuid || message.id;
    if (id) {
      const key = `${event}:${message.company_uuid || ''}:${id}`;
      if (this.recentFinancialNotificationIds.has(key)) return;
      this.recentFinancialNotificationIds.add(key);
      if (this.recentFinancialNotificationIds.size > 100) {
        this.recentFinancialNotificationIds.delete(this.recentFinancialNotificationIds.values().next().value);
      }
    }
    (event === 'new_deposit' ? this.newDepositSubject : this.newWithdrawSubject).next(message);
    this.newMessageBeep('notification');
  }

  newMessageBeep(tone = "notification_tone") {
    const volume = parseFloat(localStorage.getItem('volume') || '0.5');

    let audio = this.audioCache.get(tone);
    if (!audio) {
      audio = new Audio(`/assets/${tone}.mp3`);
      audio.load();
      this.audioCache.set(tone, audio);
    }

    audio.volume = volume;
    audio.muted = false;
    try { audio.currentTime = 0; } catch (_) {}
    const p = audio.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => {});
    }
  }

  // Call inside a user-gesture context (e.g. login/company-select) so the
  // browser unlocks audio playback for ALL tones we might ring later.
  // Plays `audibleTone` audibly; silently primes every other tone.
  primeAudio(audibleTone = "notification") {
    const tones = ["notification", "notification_tone", "iphone"];
    for (const tone of tones) {
      let audio = this.audioCache.get(tone);
      if (!audio) {
        audio = new Audio(`/assets/${tone}.mp3`);
        audio.load();
        this.audioCache.set(tone, audio);
      }
      if (tone === audibleTone) {
        this.newMessageBeep(tone);
      } else {
        audio.muted = true;
        const p = audio.play();
        if (p && typeof p.then === 'function') {
          p.then(() => {
            audio!.pause();
            try { audio!.currentTime = 0; } catch (_) {}
            audio!.muted = false;
          }).catch(() => { audio!.muted = false; });
        }
      }
    }
  }

  trackUnreadChat(room_id: string, name: string) {
    if (!this.chatUnreadSince.has(room_id)) {
      this.chatUnreadSince.set(room_id, { since: new Date(), name, room_id });
    }
    this.ensureReminderInterval();
  }

  clearUnreadChat(room_id: string) {
    this.chatUnreadSince.delete(room_id);
  }

  getChatReminder() {
    return this.chatReminderSubject.asObservable();
  }

  private ensureReminderInterval() {
    if (this.chatReminderInterval) return;
    this.chatReminderInterval = setInterval(() => this.checkChatReminders(), 30000);
  }

  private checkChatReminders() {
    const enabled = localStorage.getItem('chat_reminder_enabled');
    if (enabled === 'false') return;
    const minutes = parseInt(localStorage.getItem('chat_reminder_minutes') || '15');
    const threshold = minutes * 60 * 1000;
    const now = Date.now();
    const overdue: any[] = [];
    this.chatUnreadSince.forEach((v) => {
      if (now - v.since.getTime() >= threshold) overdue.push(v);
    });
    if (overdue.length > 0) {
      this.chatReminderSubject.next(overdue);
      this.showReminderNotification(overdue);
      // Reset since so it won't fire again until next message
      overdue.forEach(v => this.chatUnreadSince.delete(v.room_id));
    }
  }

  private showReminderNotification(overdue: any[]) {
    if (!('Notification' in window)) return;
    const names = overdue.map(v => v.name).join(', ');
    const show = () => {
      new Notification('Chat Follow-up Reminder', {
        body: `Unread messages from: ${names}`,
        icon: '/assets/icons/icon-72x72.png'
      });
    };
    if (Notification.permission === 'granted') {
      show();
    } else if (Notification.permission !== 'denied') {
      Notification.requestPermission().then(p => { if (p === 'granted') show(); });
    }
  }

  public toast(content: any, type = "success", template: any = null, id: any = null) {
    if (type === 'default') {
      this.toastr.show(content);
    } else if (type === 'error') {
      this.toastr.error(content);
    } else if (type === 'warning') {
      this.toastr.warning(content);
    } else if (type === 'info') {
      this.toastr.info(content);
    } else {
      this.toastr.success(content);
    }
  }

  public notifyPersistentError(message: string, title: string, toastClass: string = '') {
    this.toastr.error(message, title, {
      timeOut: 0,
      extendedTimeOut: 0,
      closeButton: true,
      toastClass: `ngx-toastr ${toastClass}`.trim(),
    });
  }

  public setAdminToken(token: string) {
    if (this.isViewingUser) return Promise.resolve(null);
    if (!token) {
      return Promise.resolve(null);
    }
    return this.post('/admin/notification/token/save', { token: token });
  }
}
